import { FC, useCallback, useState } from "react";
import classNames from "classnames";
import { RouteProps, useLocation } from "react-router-dom";
import styles from "./Layout.module.scss";
import { ReactComponent as HomeIcon } from "../../../assets/icons/layout/Home.svg";
import { ReactComponent as DriversIcon } from "../../../assets/icons/layout/Drivers.svg";
import { ReactComponent as HistoryIcon } from "../../../assets/icons/layout/History.svg";
import { ReactComponent as ProfileIcon } from "../../../assets/icons/layout/User.svg";
import { ReactComponent as MapIcon } from "../../../assets/icons/layout/map.svg";
import { ReactComponent as MenuIcon } from "../../../assets/icons/menuMobile.svg";
import { ReactComponent as MenuCloseIcon } from "../../../assets/icons/menuclose.svg";
import { ReactComponent as BallonIcon } from "../../../assets/icons/layout/Ballon.svg";
import { ReactComponent as LogOutIcon } from "../../../assets/icons/layout/LogOut.svg";
import { ReactComponent as DownIcon } from "../../../assets/icons/layout/down.svg";
import { ReactComponent as PlusIcon } from "../../../assets/icons/layout/plus.svg";
import { Routes } from "../../../constants/config";
import MenuItem from "./parts/MenuItem/MenuItem";
import AccountItem from "./parts/AccountItem/AccountItem";
import AuthModal from "./parts/AuthModal/AuthModal";
import { useAppDispatch, useAppSelector } from "../../../hooks/store";
import { logout, selectIsAuth, switchAccount, removeAccount } from "../../../store/slice/auth";

export type MenuItemType = {
  icon: JSX.Element;
  label: TextLayout;
  route: Routes;
  subRoutes?: Routes[];
};
export type AccountItemType = {
    id: number;
    name: string;
    token: string;
};
export enum TextLayout {
  HOME = "Главная",
  REPORT = "История заправок",
  BALLONS = "Баллоны",
  HISTORY = "История операций",
  PROFILE = "Профиль",
  MAP = "Карта АГЗС",
  WASH_MAP = "Карта Моек",
}

const _MENU: MenuItemType[] = [
  {
    icon: <HomeIcon />,
    label: TextLayout.HOME,
    route: Routes.HOME,
    subRoutes: [Routes.DRIVERS],
  },
  {
    icon: <DriversIcon />,
    label: TextLayout.REPORT,
    route: Routes.REPORT,
  },
  {
    icon: <BallonIcon />,
    label: TextLayout.BALLONS,
    route: Routes.BALLONS,
  },
  {
    icon: <HistoryIcon />,
    label: TextLayout.HISTORY,
    route: Routes.HISTORY,
  },
  {
    icon: <ProfileIcon />,
    label: TextLayout.PROFILE,
    route: Routes.PROFILE,
  },
  {
    icon: <MapIcon />,
    label: TextLayout.MAP,
    route: Routes.MAP,
  },
  {
      icon: <MapIcon />,
      label: TextLayout.WASH_MAP,
      route: Routes.WASH_MAP,
  },
];

const Layout: FC<RouteProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const isAuth = useAppSelector(selectIsAuth);
  const blockedScreen = useAppSelector((state) => state.app.blockedPage);
  const location = useLocation().pathname as Routes;
  const [isMenuOpen, setIsMenuOpen] = useState(true);
  const companyName = useAppSelector((state) => state.auth.user ? state.auth.user.companyName : '');
  const token = useAppSelector((state) => state.auth.accessToken);
  const [accounts, setAccounts] = useState<AccountItemType[]>(Object.values(useAppSelector((state) => state.auth.accounts)));
  const [isAccountSelectOpen, setIsAccountSelectOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const toggleMenu = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
  }, [isMenuOpen]);
  /**
   * logout
   */
  const onLogOut = useCallback(async () => {
    dispatch(logout());
    window.location.reload();
  }, []);

  const onAccountDropdown = useCallback(() => {
    setIsAccountSelectOpen(!isAccountSelectOpen);
  }, [isAccountSelectOpen]);

  const onAccountSelect = useCallback((id: number, isActive: boolean) => {
    if (!isActive) {
      dispatch(switchAccount(id));
      window.location.reload();
    }
  }, []);
  const onAccountRemove = useCallback((id: number, isActive: boolean) => {
    if (!isActive) {
      setAccounts(items => items.filter(item => item.id !== id));
      dispatch(removeAccount(id));
    }
  }, []);
  const toggleAuthModal = useCallback(() => {
      setIsAuthModalOpen(!isAuthModalOpen);
  }, [isAuthModalOpen]);

  const isFocused = useCallback(
    (menuItem: MenuItemType) => {
      if (blockedScreen === menuItem.route) {
        return true;
      }
      return !!menuItem?.subRoutes?.find((subRoute) => {
        const clearSubRoute = subRoute.replaceAll("/", "");
        const clearLocation = location.replaceAll("/", "");
        return clearLocation === clearSubRoute;
      });
    },
    [blockedScreen, location]
  );

  return (
    <div className={classNames(styles.root, {[styles.modalOpen]: isAuthModalOpen})}>
      <AuthModal
        isOpen={isAuthModalOpen}
        toggle={toggleAuthModal}
      />
      <div className={styles.mobileMenu} onClick={toggleMenu}>
        {isMenuOpen ? (
          <MenuIcon />
        ) : (
          <MenuCloseIcon className={styles.mobileMenuClose} />
        )}
      </div>
      <div
        className={classNames(styles.left, {
          [styles.mobileMenuHide]: isMenuOpen,
        })}
      >
        <div className={classNames(styles.block, styles.top)}>
          <img
            src={require("../../../assets/images/logo.png")}
            alt="Logo"
            className={styles.logo}
          />
        </div>
        <div className={styles.leftMenuWrapper}>
          <div className={styles.menu} onClick={toggleMenu}>
            {_MENU.map((menuItem) => (
              <MenuItem
                key={menuItem.route}
                data={menuItem}
                isFocused={isFocused(menuItem)}
                isBlocked={!!blockedScreen && blockedScreen !== menuItem.route}
              />
            ))}
          </div>
          {isAuth && (
            <div className={styles.logOutBlock}>
              <LogOutIcon className={styles.logOut} onClick={onLogOut} />
            </div>
          )}
        </div>
      </div>
      <div className={styles.right}>
        <div className={classNames(styles.block, styles.top)}>
          <div className={classNames(styles.block, styles.warning)}>
            Личный кабинет находится в режиме тестирования. Пожелания направляйте
            на email: ggroup-ural@mail.ru
          </div>
          <div className={classNames(styles.block, styles.accountContainer)}>
            <div className={classNames(styles.block, styles.accountButton)} onClick={onAccountDropdown}>
              <div className={classNames(styles.block, styles.accountName)}>
                <span>
                    {companyName}
                </span>
                <DownIcon/>
              </div>
            </div>
            <div className={classNames(styles.block, styles.accountSelect, {[styles.show]: isAccountSelectOpen})}>
              {accounts.map((account) => (
                <AccountItem
                  key={account.id}
                  id={account.id}
                  name={account.name}
                  isActive={token === account.token}
                  onSelect={onAccountSelect}
                  onRemove={onAccountRemove}
                />
              ))}
              <div className={classNames(styles.block, styles.accountAdd)} onClick={toggleAuthModal}>
                <span>Добавить аккаунт</span>
                <PlusIcon/>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.innerContent}>{children}</div>
      </div>
    </div>
  );
};
export default Layout;
